import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

const ProductsGrid = () => {
  const [produtos, setProdutos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [titulo, setTitulo] = useState('Produtos em Destaque');
  const [subtitulo, setSubtitulo] = useState('Os nossos cursos e e-books mais recomendados, selecionados especialmente para si');

  useEffect(() => {
    carregarProdutos();
    carregarConteudo();
  }, []);

  const carregarConteudo = async () => {
    try {
      const { data } = await supabase
        .from('conteudo_inicio')
        .select('titulo, subtitulo')
        .eq('secao', 'produtos')
        .maybeSingle();

      if (data) {
        if (data.titulo) setTitulo(data.titulo);
        if (data.subtitulo) setSubtitulo(data.subtitulo);
      }
    } catch (error) {
      console.error('Erro ao carregar conteúdo produtos:', error);
    }
  };

  const carregarProdutos = async () => {
    try {
      const { data, error } = await supabase
        .from('produtos')
        .select('*')
        .eq('ativo', true)
        .eq('recomendado', true)
        .order('created_at', { ascending: false })
        .limit(6);

      if (error) {
        console.error('Supabase error:', error);
        return;
      }

      if (data) {
        setProdutos(data);
      }
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!loading && produtos.length === 0) {
    return null;
  }

  // Separar título em duas partes para aplicar gradiente na última palavra
  const palavras = titulo.split(' ');
  const ultimaPalavra = palavras.pop();
  const tituloPrincipal = palavras.join(' ');

  return (
    <section className="py-24 px-8 lg:px-20 bg-luxury-black">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-400/20 to-orange-500/20 rounded-full mb-6">
            <i className="ri-star-fill text-yellow-400"></i>
            <span className="text-yellow-400 font-semibold text-sm">
              Mais Recomendados
            </span>
          </div>
          <h2 className="text-white text-4xl lg:text-6xl font-serif font-bold mb-6">
            {tituloPrincipal}{' '}
            <span className="bg-gradient-gold bg-clip-text text-transparent">
              {ultimaPalavra}
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            {subtitulo}
          </p>
        </motion.div>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
            {produtos.map((produto, index) => (
              <motion.div
                key={produto.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-dark rounded-2xl overflow-hidden border border-luxury-gold/30 hover:border-luxury-gold transition-all duration-300 hover:shadow-gold group relative"
              >
                <div className="absolute top-4 right-4 z-10">
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-yellow-400 to-orange-500 text-black flex items-center gap-1">
                    <i className="ri-star-fill"></i>
                    RECOMENDADO
                  </span>
                </div>

                <div className="relative h-64 w-full overflow-hidden">
                  <img
                    src={
                      produto.imagem_url ||
                      `https://public.readdy.ai/ai/img_res/e301dbc9b016896965cd43a002c456ec.jpg$%7BencodeURIComponent%28produto.nome%29%7D%20modern%20elegant%20minimalist%20design%20high%20quality%20professional&width=600&height=400&seq=prod${index}&orientation=landscape`
                    }
                    alt={produto.nome}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 px-4 py-2 bg-gradient-gold rounded-full">
                    <span className="text-black font-bold text-sm">
                      {produto.preco
                        ? `R$${Number(produto.preco).toFixed(2)}`
                        : 'Grátis'}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 bg-luxury-gold/20 text-luxury-gold text-xs font-semibold rounded-full">
                      {produto.tipo === 'curso' ? 'CURSO' : 'E-BOOK'}
                    </span>
                    <span className="px-3 py-1 bg-gray-800 text-gray-400 text-xs font-semibold rounded-full capitalize">
                      {produto.nivel === 'intermediario'
                        ? 'Intermédio'
                        : produto.nivel}
                    </span>
                  </div>

                  <h3 className="text-white text-xl font-bold mb-3 line-clamp-2">
                    {produto.nome}
                  </h3>

                  <p className="text-gray-400 text-sm mb-6 line-clamp-3">
                    {produto.descricao}
                  </p>

                  <Link
                    to={`/produto/${produto.id}`}
                    className="block w-full px-6 py-3 bg-gradient-gold text-black font-semibold text-center rounded-full hover:shadow-gold transition-all duration-300 hover:-translate-y-1 cursor-pointer whitespace-nowrap"
                  >
                    Ver Detalhes
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mt-12"
        >
          <Link
            to="/produtos"
            className="inline-block px-8 py-4 border-2 border-luxury-gold text-luxury-gold font-semibold rounded-full hover:bg-luxury-gold hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Ver Todos os Produtos
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductsGrid;
